/**
 * Header labels for grids fed by raw database payloads (audit detail rows, user
 * message rows).
 *
 * Those payloads only carry raw database column names (`amount_deferred_day1_cost`),
 * so this map reuses the `headerName` values already defined by the deferred-bonuses grids
 * (all-vesting-details, balances-payments-reversals, deferrals, pm-deferrals, employees,
 * funds, vesting-rules) to keep labels consistent with the rest of the module.
 *
 * Keys are written in the same camelCase form used by the grid `colId`/`field` values;
 * lookups are case- and separator-insensitive, so `fundClassName` also matches
 * `fund_class_name`. UI-only grouping prefixes (`emp_`, `annual_`, `pay_`, `rev_`, `fy_`,
 * `lab_`, `lp_`, `lr_`) are deliberately not part of the keys — they never appear in the
 * database. Anything not listed here falls back to `Abc def gh` formatting.
 */
const COLUMN_HEADER_NAMES: Record<string, string> = {
  accumulatedVestedAmountCost: 'Accumulated Vested Amount (Cost)',
  accumulatedVestedAmountFv: 'Accumulated Vested Amount (FV)',
  accumulatedVestedPct: 'Accumulated Vested %',
  accumulativeMtm: 'Accumulative MTM',
  actualPaymentDate: 'Actual payment date',
  actualRedemptionDate: 'Actual redemption date',
  amountDeferredDay1Cost: 'Amount to be deferred - Day 1 cost',
  amountDeferredRemainingCost: 'Amount to be deferred - remaining cost',
  amountReimbursedDay1: 'Amount reimbursed from fund - using Day 1 cost',
  bonusBook: 'Bonus exp was book under which book?',
  deferralId: 'Deferral ID',
  departmentAtEntitlement: 'Department (at the time of entitlement)',
  employmentEntity: 'Employment entity',
  employmentLocation: 'Employment location',
  employmentStatus: 'Employment status',
  employmentStatusYearStart: 'Employment status (at the time of entitlement)',
  endingBalanceFv: 'Ending balance of DB (in FV)',
  expectedPaymentDate: 'Expected vesting date',
  expectedRedemptionDate: 'Expected redemption date',
  expectedVestedYear: 'Expected vested year',
  fiscalYear: 'Fiscal Year',
  fundClassName: 'Fund class & Series for subscription',
  fundCostPct: '% to be borne by Fund(s)',
  fundName: 'Underlying fund invested',
  hcEmployeeId: 'HC Employee ID',
  id: 'ID',
  isActive: 'Active or not?',
  isSubjectToClawback: 'Subject to clawback',
  itdReturn: 'ITD return',
  latestEmploymentStatus: 'Latest employment status',
  latestStatus: 'Latest status',
  locationAtEntitlement: 'Employment location (at the time of entitlement)',
  pmCodeAtEntitlement: 'PM code (at the time of entitlement)',
  lyRecordCheck: 'LY record check',
  marginalAccumulatedVestingPct: 'Marginal Accumulated Vesting %',
  monthlyReturnRate: 'Monthly return rate',
  mtmAccumulationStartingMonth: 'MTM accumulation starting month',
  name: 'Name',
  natureOfBonus: 'Source of the bonus',
  newDb: 'New DB',
  openingBalanceFv: 'Opening balance of DB (in FV)',
  openingBalancePmStatement: 'Opening balance (PM statement)',
  paymentAmountCost: 'Payment amount (cost)',
  paymentAmountFv: 'Payment amount (at FV)',
  paymentDate: 'Payment date',
  paymentForVested: 'Payment for vested DB',
  performanceYear: 'For the performance of',
  pmCode: 'PM code',
  redemptionDate: 'Redemption date',
  redemptionMonth: 'Redemption month',
  redemptionMonthOffset: 'Redemption Month Offset',
  reimbursementPctDay1: 'Reimbursement % - in Day 1',
  remainingVestedAmountCost: 'Remaining Unvested Amount (Cost)',
  remainingVestedAmountFv: 'Remaining Unvested Amount (FV)',
  remainingVestedPct: 'Remaining Unvested %',
  remark: 'Remark',
  reversalAmountCost: 'Reversal amount (cost)',
  reversalAmountFv: 'Reversal amount (at FV)',
  reversalMonthFundEnd: 'Reversal month in fund end',
  reversalOfDb: 'Reversal of DB',
  reversalReason: 'Reversal reason',
  reversalRebateMgrPortion: 'Reversal\'s rebate (mgr portion)',
  reversalRebatePct: 'Reversal month\'s rebate %',
  reversalReimbursementIncome: 'Reversal of reimbursement income',
  subDepartment: 'Sub Department',
  subDepartmentAtEntitlement: 'Sub Department (at the time of entitlement)',
  subjectToRiArrangementWithTheFunds: 'Subject to RI arrangement with the funds',
  team: 'Sub Department',
  relocationDate: 'Relocation date',
  relocationHistoricalRecord: 'Relocation history',
  terminationDate: 'Termination date',
  titleAtEntitlement: 'Title (at the time of entitlement)',
  tradingPlOfPm: 'Trading P&L of PM',
  vestedAmountCost: 'Vested Amount (Cost)',
  vestedAmountFv: 'Vested Amount (FV)',
  vestedDate: 'Payment Month',
  vestedMonthOffset: 'Payment Month Offset',
  vestedPct: 'Vested %',
  vestingDate: 'Vesting Month',
  vestingMonth: 'Vesting Month',
  vestingRuleLabel: 'Vesting rule',
  vestingYear: 'Vesting Year',
  vestingYears: '# of years to be vested per HC',
  ytdMtm: 'YTD MTM',
}

/** Strips casing and separators so `fund_class_name`, `fundClassName` and `FundClassName` all collide. */
const canonicalise = (columnName: string): string => columnName.toLowerCase().replace(/[^a-z0-9]/g, '')

const HEADER_NAMES_BY_CANONICAL_KEY = new Map(
  Object.entries(COLUMN_HEADER_NAMES).map(([colId, headerName]) => [canonicalise(colId), headerName]),
)

/** Fallback formatting: `abc_def_gh` becomes `Abc def gh`. Existing casing is otherwise kept, so acronyms survive. */
const prettify = (columnName: string): string => {
  const spaced = columnName.replace(/_+/g, ' ').trim()
  if (spaced.length === 0) return columnName
  return `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`
}

/** Resolves a raw payload column name to the label used elsewhere in the deferred-bonuses grids. */
export const toColumnHeaderName = (columnName: string): string =>
  HEADER_NAMES_BY_CANONICAL_KEY.get(canonicalise(columnName)) ?? prettify(columnName)
